import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import DealerPortalInventoryCard from "../../components/dealer/DealerPortalInventoryCard";
import {
  duplicateDealerListing,
  fetchDealerInventory,
  runDealerListingAction,
} from "../../services/automapDealerInventoryApi";
import { fetchDealerFairPriceInsights } from "../../services/automapFairPriceApi";
import { fetchDealerVehicleRiskInsights } from "../../services/automapVehicleRiskApi";
import { SORT_OPTIONS, trackInventoryEvent } from "../../constants/automapDealerInventoryConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useCurrency, useLocale } from "../../intl";

function InventorySkeletonGrid() {
  return (
    <div className="dealer-portal-inventory__grid" aria-hidden="true">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="dealer-portal-inventory-card dealer-portal-inventory-card--skeleton">
          <div className="dealer-portal-inventory-card__media dealer-portal-inventory-card__skeleton-block" />
          <div className="dealer-portal-inventory-card__body">
            <div className="dealer-portal-inventory-card__skeleton-line dealer-portal-inventory-card__skeleton-line--title" />
            <div className="dealer-portal-inventory-card__skeleton-line" />
            <div className="dealer-portal-inventory-card__skeleton-line dealer-portal-inventory-card__skeleton-line--short" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DealerInventoryListPage() {
  const { dealerId } = useOutletContext();
  const { locale } = useLocale();
  const { formatMoney } = useCurrency();
  const {
    t,
    formatListingStatus,
    formatInventorySortLabel,
    translateError,
  } = useDealerPortalIntl();
  const [listings, setListings] = useState([]);
  const [fairPriceMap, setFairPriceMap] = useState({});
  const [riskMap, setRiskMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("updated_desc");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { sort };
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      const data = await fetchDealerInventory(dealerId, params);
      setListings(data.listings || []);
      try {
        const insights = await fetchDealerFairPriceInsights(dealerId);
        const map = Object.fromEntries((insights.insights || []).map((i) => [i.listingId, i]));
        setFairPriceMap(map);
      } catch {
        setFairPriceMap({});
      }
      try {
        const riskInsights = await fetchDealerVehicleRiskInsights(dealerId);
        const map = Object.fromEntries((riskInsights.insights || []).map((i) => [i.listingId, i]));
        setRiskMap(map);
      } catch {
        setRiskMap({});
      }
    } catch (err) {
      setError(translateError(err.message, "loadInventory"));
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [dealerId, search, status, sort, translateError]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (listingId, action) => {
    try {
      await runDealerListingAction(dealerId, listingId, action);
      trackInventoryEvent(`inventory_listing_${action}`, { listingId, dealerId });
      await load();
    } catch (err) {
      setError(translateError(err.message, "actionFailed"));
    }
  };

  const duplicate = async (listingId) => {
    try {
      await duplicateDealerListing(dealerId, listingId);
      trackInventoryEvent("inventory_listing_created", { listingId, dealerId, duplicate: true });
      await load();
    } catch (err) {
      setError(translateError(err.message, "duplicateFailed"));
    }
  };

  const countLabel = t("inventory.resultsCount", { count: listings.length });

  return (
    <div className="dealer-portal-inventory">
      <header className="dealer-portal-inventory__header">
        <div>
          <h2 className="dealer-portal-inventory__title">{t("nav.inventory")}</h2>
          <p className="dealer-portal-inventory__subtitle">{t("inventory.pageSubtitle")}</p>
        </div>
        <div className="dealer-portal-inventory__header-actions">
          <Link className="dealer-portal-btn primary" to="/v/automap/dealer/inventario/nuevo">
            {t("inventory.addVehicle")}
          </Link>
          <Link className="dealer-portal-btn" to="/v/automap/dealer/inventario/importar">
            {t("inventory.importCsv")}
          </Link>
        </div>
      </header>

      <section className="dealer-portal-inventory__panel" aria-label={t("nav.inventory")}>
        <div className="dealer-portal-inventory__toolbar">
          <label className="dealer-portal-inventory__search">
            <span className="dealer-portal-inventory__search-label">{t("inventory.searchPlaceholder")}</span>
            <input
              type="search"
              placeholder={t("inventory.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="dealer-portal-inventory__filter">
            <span className="dealer-portal-inventory__filter-label">{t("inventory.status")}</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t("inventory.allStatuses")}</option>
              {["draft", "active", "paused", "sold", "archived"].map((value) => (
                <option key={value} value={value}>
                  {formatListingStatus(value)}
                </option>
              ))}
            </select>
          </label>
          <label className="dealer-portal-inventory__filter">
            <span className="dealer-portal-inventory__filter-label">{t("inventory.sortLabel")}</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {formatInventorySortLabel(option.value)}
                </option>
              ))}
            </select>
          </label>
          <Link className="dealer-portal-btn dealer-portal-inventory__demand-link" to="/v/automap/dealer/inventario/demanda">
            {t("buyerInterest.demandPanelTitle")}
          </Link>
        </div>

        {!loading && listings.length > 0 ? (
          <p className="dealer-portal-inventory__count">{countLabel}</p>
        ) : null}

        {error ? <p className="dealer-state-box dealer-portal-inventory__message">{error}</p> : null}
        {loading ? (
          <>
            <p className="dealer-portal-inventory__status">{t("inventory.loading")}</p>
            <InventorySkeletonGrid />
          </>
        ) : null}

        {!loading && listings.length === 0 ? (
          <div className="dealer-state-box dealer-portal-inventory__empty">
            <p>{t("inventory.empty")}</p>
            <Link className="dealer-portal-btn primary" to="/v/automap/dealer/inventario/nuevo">
              {t("inventory.addVehicle")}
            </Link>
          </div>
        ) : null}

        {!loading && listings.length > 0 ? (
          <div className="dealer-portal-inventory__grid">
            {listings.map((listing) => (
              <DealerPortalInventoryCard
                key={listing.id}
                listing={listing}
                fairPriceInsight={fairPriceMap[listing.id]}
                riskInsight={riskMap[listing.id]}
                formatMoney={formatMoney}
                locale={locale}
                t={t}
                formatListingStatus={formatListingStatus}
                onAction={runAction}
                onDuplicate={duplicate}
                dealerId={dealerId}
              />
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}
